import React from 'react';
import { useData } from '../lib/DataContext.jsx';
import { KpiCard, SectionTitle, SrcBanner, Chip, ProvenanceFooter, ProvenanceBadge } from '../components/ui/kits.jsx';
import { fmt, fmtInt } from '../lib/format.js';
import { Check, TrendingUp, Droplet, Sprout, Users, GraduationCap, Home } from 'lucide-react';

const ICONS = { crop: Sprout, people: Users, water: Droplet, edu: GraduationCap, home: Home, default: TrendingUp };

export default function Goals() {
  const { data, attainment } = useData();
  if (!data) return null;
  const g = data.goals;
  const attMap = attainment ? Object.fromEntries(attainment.items.map((x) => [x.idx, x])) : {};

  const withRealData = attainment ? attainment.items.filter((x) => x.target).length : 0;
  const milestonesCount = g.items.filter((i) => i.milestone).length;

  return (
    <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }} data-testid="page-goals">
      <SrcBanner source={g.source} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
        <KpiCard testid="kpi-goals" hero label="Stated Goals" value={String(g.items.length)} sub="Board-approved ambitions" />
        <KpiCard testid="kpi-goal-projects" label="Projects Contributing" value={fmtInt(g.totalProjects)} sub="Across all goals" />
        <KpiCard testid="kpi-milestones" label="Milestones Reported" value={String(milestonesCount)} sub={`${g.items.length - milestonesCount} pending`} />
        {attainment && (
          <KpiCard testid="kpi-attainment"
            label={<>Real Attainment Data <ProvenanceBadge kind="live" /></>}
            value={`${withRealData}/${g.items.length}`}
            sub="Goals with quarterly MIS numbers"
          />
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 14 }}>
        {g.items.map((item, i) => {
          const Icon = ICONS[item.icon] || ICONS.default;
          const att = attMap[i];
          return (
            <div key={i} className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }} data-testid={`goal-${i}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 8 }}>
                <Chip color="#004c8f">{item.focus}</Chip>
                {item.met && (
                  <span className="pill" style={{ background: '#1f8a4c22', color: '#1f8a4c', borderColor: '#1f8a4c44' }}>
                    <Check size={12} /> Met
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'start' }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, background: '#e4eef7',
                  color: '#004c8f', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Icon size={20} />
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.4 }}>{item.goal}</div>
              </div>
              {item.milestone && (
                <div style={{
                  background: '#1f8a4c15', color: '#1f8a4c', padding: '8px 10px',
                  borderRadius: 8, fontSize: 12, fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #1f8a4c33',
                }}>
                  <TrendingUp size={14} /> {item.milestone} <span style={{ opacity: 0.7, fontWeight: 500 }}>(reported, cumulative)</span>
                </div>
              )}

              {att && att.target && (
                <QuarterlyAttainment att={att} testid={`att-${i}`} />
              )}

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--ink-3)', fontWeight: 600, marginBottom: 4 }}>
                  <span>Projects contributing</span>
                  <span className="tabular" style={{ color: 'var(--ink)', fontWeight: 700 }}>{fmtInt(item.projects)}</span>
                </div>
                <div style={{ height: 8, background: 'var(--line-2)', borderRadius: 4, overflow: 'hidden' }}>
                  <div className="anim-grow" style={{
                    width: `${Math.min(100, item.projects / 7.1)}%`, height: '100%',
                    background: 'linear-gradient(90deg, #004c8f, #0a63b0)',
                  }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <ProvenanceFooter text={attainment
        ? `Quarterly attainment is REAL — computed from ${attainment.source}. Cumulative % = (sum of Q1..Qn actuals) ÷ programme target for the ambition. All ${withRealData} goals with reported MIS output rows are represented.`
        : 'No fabricated quarterly percentages are shown. MIS quarterly attainment data was inconsistent at the time of build; the board-level view presents ambition, reported milestone, and project breadth per goal.'
      } />
    </div>
  );
}

function QuarterlyAttainment({ att, testid }) {
  const { cumulativePctByQuarter: cum, actualsByQuarter: q, target } = att;
  // Cap displayed bar width at 100% so a spike doesn't blow layout; still show real % in label
  return (
    <div style={{
      background: 'var(--surface-2)', border: '1px solid var(--line)',
      borderRadius: 10, padding: '10px 12px',
    }} data-testid={testid}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
          Quarterly Attainment · {att.fyUsed}
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-4)' }} className="tabular">
          target {fmtInt(target)}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
        {['Q1', 'Q2', 'Q3', 'Q4'].map((label, i) => {
          const pct = cum[i];
          const barPct = Math.min(100, pct);
          const color = pct >= 90 ? '#1f8a4c' : pct >= 50 ? '#5aa06a' : pct >= 25 ? '#d98a1f' : '#c8452f';
          return (
            <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-3)' }}>{label}</div>
                <div className="tabular" style={{ fontSize: 11.5, fontWeight: 800, color }}>{fmt(pct, 1)}%</div>
              </div>
              <div style={{ height: 6, background: 'var(--line-2)', borderRadius: 3, overflow: 'hidden' }}>
                <div className="anim-grow" style={{ width: `${barPct}%`, height: '100%', background: color, borderRadius: 3 }} />
              </div>
              <div style={{ fontSize: 9.5, color: 'var(--ink-4)' }} className="tabular">
                +{fmtInt(q[i])}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
